import React, { useState, useEffect } from 'react'
import { Play } from 'lucide-react'
import toast from 'react-hot-toast'
import usePlaybackStore from '../stores/playbackStore'

const EMPTY_QUEUE_TOAST_ID = 'up-next-empty-queue'

const UpNextPopup = ({ nextSong, onNext, visible = false, remainingSeconds = 10, controlsVisible = false, currentSong = null }) => {
  const cursor = usePlaybackStore(state => state.cursor)
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)
  const [isClicked, setIsClicked] = useState(false)

  // Get thumbnail URL for next song
  const nextYoutubeId = nextSong?.youtubeId ?? nextSong?.song?.youtubeId ?? null
  const thumbnailUrl = nextYoutubeId
    ? `/uploads/songs/${nextYoutubeId}/thumbnail.webp`
    : null

  // Handle thumbnail loading
  useEffect(() => {
    setThumbnailLoaded(false)
    if (thumbnailUrl) {
      const img = new Image()
      img.onload = () => {
        setThumbnailLoaded(true)
      }
      img.onerror = () => {
        console.warn('🖼️ Failed to load thumbnail for up next popup:', {
          url: thumbnailUrl
        })
        setThumbnailLoaded(false)
      }
      img.src = thumbnailUrl
    }
  }, [thumbnailUrl])

  // Handle click to skip
  const handleClick = async () => {
    if (!nextSong || isClicked) return

    console.log('🎵 Up Next popup clicked - skipping to next song')
    setIsClicked(true)

    try {
      await onNext(cursor)
      // Reset clicked state after successful operation
      setTimeout(() => {
        setIsClicked(false)
      }, 500) // Small delay to show the clicked animation
    } catch (error) {
      console.error('🎵 Failed to skip via Up Next popup:', error)
      setIsClicked(false)
    }
  }

  const handleEmptyClick = () => {
    toast('Queue more songs to keep the party going!', {
      id: EMPTY_QUEUE_TOAST_ID
    })
  }

  // Reset clicked state when visibility changes
  useEffect(() => {
    if (!visible) {
      setIsClicked(false)
    }
  }, [visible])

  // Reset clicked state when next song changes (successful skip)
  useEffect(() => {
    setIsClicked(false)
  }, [nextSong?.songId, nextYoutubeId])

  // Show when controls visible OR when countdown popup should be visible
  const shouldShow = (controlsVisible && (nextSong || currentSong)) || (visible && nextSong && !controlsVisible)

  if (!shouldShow) {
    return null
  }

  // Determine positioning based on controls visibility
  const positioningStyle = controlsVisible
    ? {
      bottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
      right: 'calc(2rem + env(safe-area-inset-right, 0px))'
    }
    : {
      bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
      right: 'calc(1.5rem + env(safe-area-inset-right, 0px))'
    }

  // Determine sizing and animation based on mode
  const sizingClasses = controlsVisible
    ? 'w-60 max-w-60 h-24' // Match empty state footprint when controls are visible
    : 'w-80' // Larger for countdown popup

  const animationClasses = controlsVisible
    ? 'opacity-100 transform translate-x-0 translate-y-0' // Always visible when controls are up
    : `transition-all duration-500 transform ${visible && !isClicked ? 'translate-x-0 translate-y-0 opacity-100' : 'translate-x-full translate-y-0 opacity-0'
    }`

  return (
    <div
      className={`fixed z-[60] ${animationClasses}`}
      style={positioningStyle}
    >
      <div
        onClick={nextSong ? handleClick : handleEmptyClick}
        className={`
          relative overflow-hidden rounded-lg ${sizingClasses}
          transition-all duration-300 ease-out
          cursor-pointer flex flex-col
          ${nextSong && isClicked ? 'scale-95 opacity-75' : nextSong ? 'hover:scale-105 shadow-2xl' : ''}
          bg-white/10 backdrop-blur-sm
        `}
        role="button"
        aria-label={nextSong ? `Skip to next song: ${nextSong.song?.title || nextSong.title}` : "Queue empty: add more songs"}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (nextSong) {
              handleClick()
            } else {
              handleEmptyClick()
            }
          }
        }}
      >
        {/* Thumbnail Background */}
        {thumbnailUrl && thumbnailLoaded && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${thumbnailUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}

        {/* Gradient Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />

        {/* Content */}
        <div className="relative p-4 flex flex-col flex-1">
          {nextSong ? (
            controlsVisible ? (
              <div className="flex-1 flex flex-col">
                <div className="text-xs text-white/70 font-medium uppercase tracking-wide">
                  Up Next
                </div>
                <div className="mt-1 flex flex-col gap-1">
                  <h3 className="text-sm font-bold text-white leading-tight line-clamp-1">
                    {nextSong.song?.title || nextSong.title || 'Unknown Title'}
                  </h3>
                  <p className="text-xs text-white/80 leading-tight line-clamp-1">
                    {nextSong.song?.artist || nextSong.artist || 'Unknown Artist'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="text-xs text-white/70 font-medium uppercase tracking-wide">
                      Up Next
                    </div>
                    {/* Countdown indicator - only show during countdown popup */}
                    {!controlsVisible && remainingSeconds > 0 && (
                      <div className="flex items-center space-x-1">
                        <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse" />
                        <div className="text-xs text-green-400 font-medium">
                          {Math.ceil(remainingSeconds)}s
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Click indicator - only show for next song */}
                  {!controlsVisible && (
                    <div className="flex items-center space-x-2">
                      <Play className="w-4 h-4 text-white/60" />
                      <div className="text-xs text-white/60 font-medium">
                        CLICK TO SKIP
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <h3 className={`font-bold text-white leading-tight line-clamp-2 ${controlsVisible ? 'text-sm' : 'text-base'
                    }`}>
                    {nextSong.song?.title || nextSong.title || 'Unknown Title'}
                  </h3>
                  <p className={`text-white/80 leading-tight line-clamp-1 ${controlsVisible ? 'text-xs mt-1' : 'text-sm'
                    }`}>
                    {nextSong.song?.artist || nextSong.artist || 'Unknown Artist'}
                  </p>
                </div>
              </div>
            )
          ) : (
            // No next song case (only when controls are visible)
            <div className="flex-1 flex flex-col">
              <div className="text-xs text-white/60 font-medium uppercase tracking-wide mb-2">
                Queue Status
              </div>
              <div className="text-sm text-white/80">
                No more songs queued
              </div>
              <div className="text-xs text-white/60 mt-1">
                Add songs to continue
              </div>
            </div>
          )}

          {/* Progress bar for remaining time - only show during countdown popup */}
          {!controlsVisible && nextSong && remainingSeconds > 0 && (
            <div className="mt-3 w-full bg-white/20 rounded-full h-1">
              <div
                className="bg-green-400 h-1 rounded-full transition-all duration-1000 ease-linear"
                style={{
                  width: `${100 - (remainingSeconds / 10) * 100}%`
                }}
              />
            </div>
          )}
        </div>

        {/* Subtle border highlight */}
        <div className="absolute inset-0 rounded-lg ring-1 ring-white/20 pointer-events-none" />
      </div>
    </div>
  )
}

export default UpNextPopup
